import { Heading } from "@ariakit/react";
import { atomic } from "../atomic.css";
import { DatabaseQueryOpFailure } from "../database-provider";
import { NetworkQueryOpFailure } from "../hooks/use-fetch-shared-army";

export function ErrorView(props: {
  heading?: string;
  description?: string;
  code?: string;
}) {
  return (
    <div
      className={atomic({
        textAlign: "center",
        padding: {
          default: 24,
          medium: 56,
        },
      })}
    >
      <Heading
        className={atomic({
          fontFamily: "barlow-condensed",
          fontWeight: "semi-bold",
          fontSize: 32,
          marginBlockEnd: 12,
          lineHeight: 1,
        })}
      >
        {props.heading ?? "Oops! Something went wrong"}
      </Heading>

      <p
        className={atomic({
          fontSize: 18,
          lineHeight: 1.5,
          maxInlineSize: "optimal-reading",
          marginInline: "auto",
        })}
      >
        {props.description ??
          "Un unexpected error occurred — sorry about that!"}
      </p>

      {props.code && (
        <div className={atomic({ fontSize: 12, marginBlockStart: 8 })}>
          Code: {props.code}
        </div>
      )}
    </div>
  );
}

export function KnownErrorView(props: {
  code:
    | NetworkQueryOpFailure["type"]
    | DatabaseQueryOpFailure["type"]
    | "ARMY_NOT_FOUND";
}) {
  switch (props.code) {
    case "DB_VERSION_HIGHER_THAN_REQUESTED": {
      return (
        <ErrorView
          heading="Database query error"
          description="Could not open database. Try refreshing the page."
          code={props.code}
        />
      );
    }

    case "CLOSED_FOR_UPGRADE": {
      return (
        <ErrorView
          heading="Database query error"
          description="Database was closed due to version upgrade. Please refresh the page."
          code={props.code}
        />
      );
    }

    case "UPGRADE_BLOCKED": {
      return (
        <ErrorView
          heading="Database query error"
          description="Database could not be opened due to an upgrade being blocked by other browser tabs. Please close other browser tabs with this application and then refresh the page."
          code={props.code}
        />
      );
    }

    case "QUERY_ERROR": {
      return (
        <ErrorView
          heading="Database query error"
          description="Database query failed. Sorry about this. Perhaps try refreshing the page?"
          code={props.code}
        />
      );
    }

    case "ARMY_NOT_FOUND": {
      return (
        <ErrorView
          heading="Army not found"
          description="The link you followed might be broken, or the army you were looking for was removed."
        />
      );
    }

    case "NETWORK_FAILURE": {
      return (
        <ErrorView
          heading="No connection"
          description="Check your internet connection and try again. If the issue persists, please get in touch with 'artmllr' via Discord."
        />
      );
    }

    case "UNEXPECTED_API_FAILURE": {
      return (
        <ErrorView
          heading="Oops! Our servers are misbehaving"
          description="Try refreshing the app. If the issue persists, please get in touch with 'artmllr' via Discord."
        />
      );
    }

    case "UNKNOWN_ERROR":
    default: {
      return (
        <ErrorView
          heading="Oops! Something went wrong"
          description="If the issue persists, please get in touch with 'artmllr' via Discord."
          code={props.code}
        />
      );
    }
  }
}
